import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";

import actions from "@/actions";

import { SettingsModal } from "../settings-modal";
import {
    DimTestConfig,
    DimTestLoading,
    GroupSettingsProgress,
    PublishAllConfirm,
    SelectorGroupList,
} from ".";

const useDimTestStatus = (
    dimTestStatus,
    showMessage,
    dimTestState,
    setDimTestState,
) =>
    useEffect(() => {
        const { status, message } = dimTestStatus;

        if (
            dimTestState !== DimTestConfig.states.loading ||
            (status !== "success" && status !== "error")
        ) {
            return;
        }

        if (message) {
            showMessage(message, status === "success" ? "info" : "error");
        }

        setDimTestState(null);
    }, [dimTestState, dimTestStatus, setDimTestState, showMessage]);

const useSettingsStatus = (lightGroupSettingsStatus, showMessage, progress) =>
    useEffect(() => {
        const { message, success } = lightGroupSettingsStatus ?? {};

        if (success === undefined) {
            return;
        }

        if (
            message &&
            (!success || !progress || progress.current === progress.total)
        ) {
            showMessage(message, success ? "info" : "error");
        }
    }, [lightGroupSettingsStatus, progress, showMessage]);

const GroupSelector = ({
    onSelect,
    onAdd,
    lightGroups,
    lights,
    startDimTest,
    dimTestStatus,
    showMessage,
    lightGroupSettings,
    setEditingLightGroupSettings,
    saveAndPublishLightGroupSettings,
    saveLightGroupSettings,
    setLightGroupSettingsPublishAllConfirming,
    lightGroupSettingsPublishAllConfirming,
    publishAllLightGroupSettings,
    pendingLightGroupSettings,
    lightGroupSettingsStatus,
    lightGroupSettingsProgress,
    setLightGroupProgress,
    floorspaceFileUrl,
}) => {
    const [dimTestState, setDimTestState] = useState(null);
    const [dimTestGroup, setDimTestGroup] = useState(null);
    const [dimTestLightLevel, setDimTestLightLevel] = useState(60);
    const [isSettingsModalOpened, setSettingsModalOpened] = useState(false);

    const handleStartDimTest = (group) => {
        setDimTestState(DimTestConfig.states.confirm);
        setDimTestGroup(group);
    };

    const handleConfirmDimTest = () => {
        setDimTestState(DimTestConfig.states.loading);
        startDimTest({ group: dimTestGroup, lightLevel: dimTestLightLevel });
    };

    const handleOpenSettings = (group) => {
        setEditingLightGroupSettings(lightGroupSettings[group.groupID]);
        setSettingsModalOpened(true);
    };

    const _handleSaveSettings = () => {
        setSettingsModalOpened(false);
        setLightGroupProgress(null);
    };

    const handleSaveAndPublishSettings = () => {
        _handleSaveSettings();
        saveAndPublishLightGroupSettings();
    };

    const handleSaveSettings = () => {
        _handleSaveSettings();
        saveLightGroupSettings();
    };

    const handlePublishAll = () => {
        setLightGroupSettingsPublishAllConfirming(true);
    };

    const handlePublishAllConfirm = () => {
        setLightGroupSettingsPublishAllConfirming(false);
        publishAllLightGroupSettings();
    };

    const handlePublishAllCancel = () => {
        setLightGroupSettingsPublishAllConfirming(false);
    };

    const handleDownloadFloorspaceFile = () => {
        if (!floorspaceFileUrl) {
            return;
        }
        window.open(floorspaceFileUrl, "_blank");
    };

    useDimTestStatus(dimTestStatus, showMessage, dimTestState, setDimTestState);
    useSettingsStatus(
        lightGroupSettingsStatus,
        showMessage,
        lightGroupSettingsProgress,
    );

    return (
        <>
            <SelectorGroupList
                onAdd={onAdd}
                onPublishAll={handlePublishAll}
                onDownloadFloorspaceFile={handleDownloadFloorspaceFile}
                floorspaceFileUrl={floorspaceFileUrl}
                lightGroups={lightGroups}
                lights={lights}
                onSelect={onSelect}
                onStartDimTest={handleStartDimTest}
                onOpenSettings={handleOpenSettings}
            />

            {dimTestState &&
                ReactDOM.createPortal(
                    <DimTestLoading
                        setDimTestState={setDimTestState}
                        dimTestState={dimTestState}
                        onConfirmDimTest={handleConfirmDimTest}
                        dimTestGroup={dimTestGroup}
                        setDimTestLightLevel={setDimTestLightLevel}
                        dimTestLightLevel={dimTestLightLevel}
                    />,
                    document.body,
                )}

            {ReactDOM.createPortal(
                <SettingsModal
                    visible={isSettingsModalOpened}
                    onClose={() => setSettingsModalOpened(false)}
                    onSaveAndPublish={handleSaveAndPublishSettings}
                    onSave={handleSaveSettings}
                />,
                document.body,
            )}

            {pendingLightGroupSettings &&
                ReactDOM.createPortal(
                    <GroupSettingsProgress
                        lightGroupSettingsStatus={lightGroupSettingsStatus}
                        lightGroupSettingsProgress={lightGroupSettingsProgress}
                    />,
                    document.body,
                )}

            {lightGroupSettingsPublishAllConfirming &&
                ReactDOM.createPortal(
                    <PublishAllConfirm
                        onPublishAllConfirm={handlePublishAllConfirm}
                        onPublishAllCancel={handlePublishAllCancel}
                    />,
                    document.body,
                )}
        </>
    );
};

GroupSelector.propTypes = {
    onSelect: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
    lightGroups: PropTypes.array.isRequired,
    lights: PropTypes.array.isRequired,
    startDimTest: PropTypes.func.isRequired,
    dimTestStatus: PropTypes.object.isRequired,
    showMessage: PropTypes.func.isRequired,
    lightGroupSettings: PropTypes.object.isRequired,
    setEditingLightGroupSettings: PropTypes.func.isRequired,
    saveAndPublishLightGroupSettings: PropTypes.func.isRequired,
    saveLightGroupSettings: PropTypes.func.isRequired,
    publishAllLightGroupSettings: PropTypes.func.isRequired,
    setLightGroupSettingsPublishAllConfirming: PropTypes.func.isRequired,
    lightGroupSettingsPublishAllConfirming: PropTypes.bool,
    pendingLightGroupSettings: PropTypes.object,
    lightGroupSettingsStatus: PropTypes.object,
    lightGroupSettingsProgress: PropTypes.object,
    setLightGroupProgress: PropTypes.func.isRequired,
    floorspaceFileUrl: PropTypes.string,
};

const mapStateToProps = ({ map }) => ({
    lightGroups: map.general.lightGroups,
    lights: Object.values(map.values.global.present.lights),
    dimTestStatus: map.general.dimTest,
    lightGroupSettings: map.general.lightGroupSettings,
    pendingLightGroupSettings: map.general.pendingLightGroupSettings,
    lightGroupSettingsStatus: map.general.lightGroupSettingsStatus,
    lightGroupSettingsProgress: map.general.lightGroupSettingsProgress,
    lightGroupSettingsPublishAllConfirming:
        map.general.lightGroupSettingsPublishAllConfirming,
    floorspaceFileUrl: map.general.floorspaceFileUrl,
});

const mapDispatchToProps = {
    startDimTest: actions.map.general.startDimTest,
    showMessage: actions.window.showMessage,
    setEditingLightGroupSettings:
        actions.map.general.setEditingLightGroupSettings,
    saveAndPublishLightGroupSettings:
        actions.map.general.saveAndPublishLightGroupSettings,
    saveLightGroupSettings: actions.map.general.saveLightGroupSettings,
    publishAllLightGroupSettings:
        actions.map.general.publishAllLightGroupSettings,
    setLightGroupSettingsPublishAllConfirming:
        actions.map.general.setLightGroupSettingsPublishAllConfirming,
    setLightGroupProgress: actions.map.general.setLightGroupSettingsProgress,
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupSelector);
